import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowUp } from "lucide-react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useIsMobileLike } from "../../../../../hooks/useIsMobileLike";
import { logCustomEvent } from "../../../../../services/analytics/analytics";
import { processRecordInput } from "../../../services/ai";
import mapRecordsErrorToKey from "../../../utils/recordsErrorMapper";
import styles from "./AIAssistant.module.css";

const PROMPT_LIMIT = 500;
const REQUEST_PHASE_LOADING = "loading";
const ASSISTANT_ERROR_KEYS_BY_CODE = {
  "invalid-argument": "records.editor.ai.errors.invalid_argument",
  "rate-limit": "records.editor.ai.errors.rate_limit",
  cancelled: "records.editor.ai.errors.cancelled",
};

const buildRequestId = () =>
  `req-${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const mapAssistantErrorKey = (error) => {
  if (!error) {
    return "records.editor.ai.errors.request_failed";
  }

  const code =
    typeof error.code === "string" && error.code.trim()
      ? error.code.trim().toLowerCase()
      : "";
  if (code && ASSISTANT_ERROR_KEYS_BY_CODE[code]) {
    return ASSISTANT_ERROR_KEYS_BY_CODE[code];
  }

  return mapRecordsErrorToKey(error, {
    fallbackKey: "records.editor.ai.errors.request_failed",
  });
};

const translateAssistantError = (t, error) => {
  const key = mapAssistantErrorKey(error);
  return t(key);
};

function AIAssistant({
  record,
  aiState,
  currentSnapshot,
  onPromptChange,
  onStatusChange,
  onProposalReady,
  onApplyProposal,
  onClearProposal,
}) {
  const { t, i18n } = useTranslation();
  const isMobileLike = useIsMobileLike();
  const [isExpanded, setIsExpanded] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");
  const textareaRef = useRef(null);
  const reviewRef = useRef(null);
  const activeRequestRef = useRef({ id: null, startedAt: 0 });
  const resolvedLanguage = i18n?.resolvedLanguage;
  const activeLanguage = i18n?.language;

  const assistantLocale = useMemo(() => {
    if (typeof resolvedLanguage === "string" && resolvedLanguage.trim()) {
      return resolvedLanguage.trim();
    }
    if (typeof activeLanguage === "string" && activeLanguage.trim()) {
      return activeLanguage.trim();
    }
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.language === "string" &&
      navigator.language.trim()
    ) {
      return navigator.language.trim();
    }
    return undefined;
  }, [resolvedLanguage, activeLanguage]);

  const assistantTimezone = useMemo(() => {
    try {
      const options = Intl.DateTimeFormat().resolvedOptions();
      if (options && typeof options.timeZone === "string" && options.timeZone.trim()) {
        return options.timeZone.trim();
      }
    } catch (_error) {
      // Ignore timezone detection failures; backend can fall back.
    }
    return undefined;
  }, []);

  // Centralised assistant environment context; extend this map as new hints are introduced.
  const assistantContext = useMemo(() => {
    const context = {};
    if (assistantLocale) {
      context.locale = assistantLocale;
    }
    if (assistantTimezone) {
      context.timezone = assistantTimezone;
    }
    return Object.keys(context).length > 0 ? context : undefined;
  }, [assistantLocale, assistantTimezone]);

  const isLoading = aiState.status === "loading";
  const isProposalReady = aiState.status === "proposalReady";
  const isError = aiState.status === "error";
  const recordId = record?.id ?? null;
  const recordState = recordId ? "existing" : "new";
  const hasPrompt = Boolean(aiState.prompt.trim());
  const shouldShowSend = hasPrompt || isLoading;
  const reviewMessage =
    aiState.message && aiState.message.trim().length > 0
      ? aiState.message.trim()
      : t(
          "records.editor.ai.review_default",
          "AI suggestion ready. Review the proposed changes in the form."
        );

  const adjustTextareaSize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const maxHeight = 160;
    textarea.style.height = `${Math.min(
      textarea.scrollHeight,
      maxHeight
    )}px`;
  }, []);

  useEffect(() => {
    adjustTextareaSize();
  }, [aiState.prompt, adjustTextareaSize]);

  useEffect(() => {
    return () => {
      activeRequestRef.current = { id: null, startedAt: 0 };
    };
  }, []);

  useEffect(() => {
    if (aiState.status === "loading") {
      setLiveMessage(
        t("records.editor.ai.live.generating", "AI assistant is generating a suggestion.")
      );
    } else if (aiState.status === "error") {
      setLiveMessage(translateAssistantError(t, aiState.lastError));
    } else if (aiState.status === "proposalReady") {
      setLiveMessage(
        aiState.message?.trim()
          ? aiState.message
          : t(
              "records.editor.ai.review_default",
              "AI suggestion ready. Review the proposed changes in the form."
            )
      );
    } else if (aiState.status === "idle") {
      setLiveMessage("");
    }
  }, [aiState.status, aiState.lastError, aiState.message, t]);

  const cancelInFlight = useCallback(
    (emitAnalytics = true) => {
      if (!activeRequestRef.current.id) {
        return;
      }

      activeRequestRef.current = { id: null, startedAt: 0 };
      onStatusChange("idle", { keepPrompt: true });

      if (emitAnalytics) {
        logCustomEvent("aiAssistant.canceled", {
          phase: REQUEST_PHASE_LOADING,
          record_state: recordState,
        });
        setLiveMessage(t("records.editor.ai.live.request_cancelled", "AI request canceled."));
      }
    },
    [onStatusChange, recordState, t]
  );

  const handleCancelProposal = useCallback(() => {
    onClearProposal();
    setLiveMessage(t("records.editor.ai.live.suggestion_dismissed", "AI suggestion dismissed."));
    logCustomEvent("aiAssistant.canceled", {
      phase: "review",
      record_state: recordState,
    });
  }, [onClearProposal, recordState, t]);

  const handleApply = useCallback(async () => {
    await Promise.resolve(onApplyProposal());
    setLiveMessage(
      t("records.editor.ai.live.suggestion_applied", "AI suggestion applied to the form.")
    );
    logCustomEvent("aiAssistant.applied", {
      record_state: recordState,
    });
    setIsExpanded(false);
  }, [onApplyProposal, recordState, t]);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    if (isProposalReady) {
      const node = reviewRef.current;
      if (!node) return;

      const focusable = node.querySelectorAll("button");
      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];
      firstElement?.focus();

      const handleKeyDown = (event) => {
        if (event.key === "Tab" && focusable.length > 0) {
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        } else if (event.key === "Enter") {
          event.preventDefault();
          handleApply();
        } else if (event.key === "Escape") {
          event.preventDefault();
          handleCancelProposal();
        }
      };

      node.addEventListener("keydown", handleKeyDown);
      return () => node.removeEventListener("keydown", handleKeyDown);
    }

    const focusTimeout = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 120);

    return () => window.clearTimeout(focusTimeout);
  }, [isExpanded, isProposalReady, handleApply, handleCancelProposal]);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handlePromptChange = useCallback(
    (event) => {
      const value = event.target.value.slice(0, PROMPT_LIMIT);
      if (isError) {
        onStatusChange("idle", { keepPrompt: true });
      }
      onPromptChange(value);
    },
    [isError, onPromptChange, onStatusChange]
  );

  const handleSubmit = useCallback(async () => {
    const trimmed = (aiState.prompt || "").trim();
    if (!trimmed || isLoading || isProposalReady) {
      return;
    }

    if (trimmed !== aiState.prompt) {
      onPromptChange(trimmed);
    }

    const requestId = buildRequestId();
    const startedAt = Date.now();
    activeRequestRef.current = { id: requestId, startedAt };

    onStatusChange("loading", {
      prompt: trimmed,
    });
    setLiveMessage(
      t("records.editor.ai.live.generating", "AI assistant is generating a suggestion.")
    );
    logCustomEvent("aiAssistant.requested", {
      prompt_length: trimmed.length,
      record_state: recordState,
    });

    try {
      const snapshot =
        currentSnapshot ?? {
          content: { title: "", body: "", tags: [] },
          attributes: {},
          links: { outgoing: [] },
        };

      const response = await processRecordInput({
        recordId: recordId ?? undefined,
        prompt: trimmed,
        currentSnapshot: snapshot,
        context: assistantContext,
      });

      if (activeRequestRef.current.id !== requestId) {
        return;
      }

      const latency = Date.now() - startedAt;

      onProposalReady({
        proposal: response.proposal,
        message: response?.message,
      });

      logCustomEvent("aiAssistant.proposal_ready", {
        latency_ms: latency,
        record_state: recordState,
      });
      setLiveMessage(
        response?.message?.trim()
          ? response.message
          : t(
              "records.editor.ai.review_default",
              "AI suggestion ready. Review the proposed changes in the form."
            )
      );
    } catch (error) {
      if (activeRequestRef.current.id !== requestId) {
        return;
      }

      const latency = Date.now() - startedAt;
      const errorCode = error?.code || "internal";

      onStatusChange("error", {
        error: {
          code: errorCode,
          message: error?.message,
        },
      });

      logCustomEvent("aiAssistant.failed", {
        error_code: errorCode,
        latency_ms: latency,
        record_state: recordState,
      });
      setLiveMessage(translateAssistantError(t, error));
    } finally {
      if (activeRequestRef.current.id === requestId) {
        activeRequestRef.current = { id: null, startedAt: 0 };
      }
    }
  }, [
    aiState.prompt,
    isLoading,
    isProposalReady,
    onPromptChange,
    onStatusChange,
    recordState,
    currentSnapshot,
    onProposalReady,
    recordId,
    assistantContext,
    t,
  ]);

  const handleRetry = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  const handlePromptKeyDown = useCallback(
    (event) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !isMobileLike &&
        !isLoading
      ) {
        event.preventDefault();
        handleSubmit();
      } else if (event.key === "Escape" && isLoading) {
        event.preventDefault();
        cancelInFlight();
      }
    },
    [isMobileLike, isLoading, handleSubmit, cancelInFlight]
  );

  const handleSendButtonClick = useCallback(() => {
    if (isLoading) {
      cancelInFlight();
    } else {
      handleSubmit();
    }
  }, [isLoading, cancelInFlight, handleSubmit]);

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.container} ${
          isExpanded ? styles.containerExpanded : ""
        }`}
      >
        <button
          type="button"
          className={`${styles.button} ${
            isExpanded ? styles.buttonExpanded : ""
          } ${isLoading ? styles.buttonProcessing : ""}`}
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls="ai-assistant-panel"
        >
          <svg
            className={styles.sparkles}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m12 3-1.9 5.8a2 2 0 0 1-1.287 1.288L3 12l5.8 1.9a2 2 0 0 1 1.288 1.287L12 21l1.9-5.8a2 2 0 0 1 1.287-1.288L21 12l-5.8-1.9a2 2 0 0 1-1.288-1.287L12 3Z" />
            <path d="M5 3v4" />
            <path d="M19 17v4" />
            <path d="M3 5h4" />
            <path d="M17 19h4" />
          </svg>
          <span>
            {isLoading
              ? t("records.editor.ai.toggle.loading", "AI is working...")
              : isProposalReady
              ? t("records.editor.ai.toggle.review", "Review AI suggestion")
              : record?.id
              ? t("records.editor.ai.toggle.edit", "Edit with AI")
              : t("records.editor.ai.toggle.create", "Create with AI")}
          </span>
          <svg
            className={styles.chevron}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div
          id="ai-assistant-panel"
          className={`${styles.panel} ${
            isExpanded ? styles.panelActive : ""
          }`}
        >
          <div className={styles.panelBody}>
            <span className={styles.srOnly} aria-live="polite">
              {liveMessage}
            </span>

            {isProposalReady ? (
              <div className={styles.reviewContainer} ref={reviewRef}>
                <div className={styles.reviewSummary}>
                  <span className={styles.reviewBadge}>
                    {t("records.editor.ai.badge", "AI assistant")}
                  </span>
                  <p className={styles.reviewMessage}>{reviewMessage}</p>
                </div>
                <div className={styles.reviewActions}>
                  <button
                    type="button"
                    className={styles.secondaryAction}
                    onClick={handleCancelProposal}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                  <button
                    type="button"
                    className={styles.primaryAction}
                    onClick={handleApply}
                  >
                    {t("records.editor.ai.apply", "Apply")}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div
                  className={[
                    styles.inputShell,
                    isLoading ? styles.inputDisabled : "",
                    isError ? styles.inputError : "",
                    shouldShowSend ? styles.inputShellShowSend : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <textarea
                    ref={textareaRef}
                    className={styles.textarea}
                    placeholder={
                      record?.id
                        ? t(
                            "records.editor.ai.prompt_placeholder_edit",
                            "Tell the assistant what to adjust in this record..."
                          )
                        : t(
                            "records.editor.ai.prompt_placeholder_create",
                            "Describe what you want to create..."
                          )
                    }
                    value={aiState.prompt}
                    onChange={handlePromptChange}
                    onKeyDown={handlePromptKeyDown}
                    disabled={isLoading}
                    rows={1}
                    maxLength={PROMPT_LIMIT}
                  />
                  <button
                    type="button"
                    className={[
                      styles.sendButton,
                      shouldShowSend ? styles.sendButtonVisible : "",
                      isLoading ? styles.sendButtonLoading : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={handleSendButtonClick}
                    disabled={!hasPrompt && !isLoading}
                    aria-hidden={!shouldShowSend}
                    tabIndex={shouldShowSend ? 0 : -1}
                  >
                    <span className={styles.srOnly}>
                      {isLoading
                        ? t(
                            "records.editor.ai.cancel_request_aria",
                            "Cancel AI assistant request"
                          )
                        : t(
                            "records.editor.ai.send_request_aria",
                            "Send prompt to AI assistant"
                          )}
                    </span>
                    {isLoading ? (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                        className={styles.sendSpinner}
                      >
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                    ) : (
                      <ArrowUp aria-hidden="true" className={styles.sendIcon} />
                    )}
                  </button>
                </div>

                {isError && (
                  <div className={styles.errorBanner} role="alert">
                    <span>{translateAssistantError(t, aiState.lastError)}</span>
                    <button
                      type="button"
                      className={styles.retryButton}
                      onClick={handleRetry}
                    >
                      {t("common.retry", "Retry")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

AIAssistant.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
  }),
  aiState: PropTypes.shape({
    status: PropTypes.oneOf(["idle", "loading", "proposalReady", "error"])
      .isRequired,
    prompt: PropTypes.string.isRequired,
    proposal: PropTypes.object,
    message: PropTypes.string,
    lastError: PropTypes.shape({
      code: PropTypes.string,
      message: PropTypes.string,
    }),
  }).isRequired,
  currentSnapshot: PropTypes.shape({
    content: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    attributes: PropTypes.shape({
      date: PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired,
        timezone: PropTypes.string.isRequired,
        isAllDay: PropTypes.bool.isRequired,
      }),
      dueDate: PropTypes.string,
      isComplete: PropTypes.bool,
      location: PropTypes.shape({
        name: PropTypes.string.isRequired,
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired,
        source: PropTypes.oneOf(["google", "mapbox", "manual"]).isRequired,
        placeId: PropTypes.string,
        context: PropTypes.string,
        geohash: PropTypes.string,
      }),
      priority: PropTypes.oneOf(["high", "medium", "low"]),
    }).isRequired,
    links: PropTypes.shape({
      outgoing: PropTypes.arrayOf(
        PropTypes.shape({
          targetId: PropTypes.string.isRequired,
          targetType: PropTypes.oneOf(["record", "external"]).isRequired,
          addedAt: PropTypes.string,
        })
      ),
    }).isRequired,
  }).isRequired,
  onPromptChange: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onProposalReady: PropTypes.func.isRequired,
  onApplyProposal: PropTypes.func.isRequired,
  onClearProposal: PropTypes.func.isRequired,
};

AIAssistant.defaultProps = {
  record: null,
};

export default AIAssistant;
